import type { ITenant, ITenantApi } from '@/type'
import axios from 'axios'

const TENANT_KEY = 'w4aTenant'
/**
 * An AdminHub "login as customer" keeps the tenant it was issued on for its own tab
 * (sessionStorage, like its token): switching in another tab must not move it.
 */
const TAB_TENANT_KEY = 'w4aTenantTab'
/** URL parameter the AdminHub's "login as customer" names its tenant with */
export const TENANT_PARAM = 'tenant'

/**
 * The backends of the Work4All tenants the hub can switch between, one per tenant: set
 * on dev and QA only (`VITE_API_BASE_URL_MANDANT_1` / `_2`). Live has a single backend,
 * so there is nothing to switch.
 */
export const tenantApis: ITenantApi[] = [
  { code: 2, url: import.meta.env.VITE_API_BASE_URL_MANDANT_2 },
  { code: 1, url: import.meta.env.VITE_API_BASE_URL_MANDANT_1 },
].filter((api): api is ITenantApi => !!api.url)

export const canSwitchTenant = tenantApis.length > 1

/** The tenant holding LA CONCEPT's real data: on dev / QA only ever read, never written. */
export const LIVE_TENANT_CODE = 1

/**
 * Whether the switcher may go to this tenant: the live one only while its backend says it
 * is read-only, so dev / QA never write LA CONCEPT's real data — not when it says
 * otherwise, nor while it does not answer.
 */
export function canSwitchTo(code: number, tenant: ITenant | undefined): boolean {
  return code !== LIVE_TENANT_CODE || tenant?.read_only === true
}

/**
 * The tenant chosen in the switcher, if it is one of `tenantApis` — this tab's own one
 * (an AdminHub session's) before the browser's.
 */
export function selectedTenantCode(): number | null {
  const known = (code: number) => tenantApis.some((api) => api.code === code)
  const tabCode = Number(sessionStorage.getItem(TAB_TENANT_KEY))
  if (known(tabCode)) return tabCode
  const code = Number(localStorage.getItem(TENANT_KEY))
  return known(code) ? code : null
}

/** Chosen in this tab's switcher: for the browser, this tab following it again. */
export function selectTenant(code: number) {
  sessionStorage.removeItem(TAB_TENANT_KEY)
  localStorage.setItem(TENANT_KEY, String(code))
}

/** An AdminHub session's tenant, kept for this tab only. */
export function keepTenantForTab(code: number) {
  sessionStorage.setItem(TAB_TENANT_KEY, String(code))
}

/**
 * The AdminHub session of this tab ended (signed out, or its token ran out): the tab
 * follows the browser's tenant again, so a login here does not land on the session's
 * tenant and sign the other tabs out. True when the backend changes with it — the page
 * must then be loaded anew, its requests go to the backend set at load.
 */
export function releaseTenantForTab(): boolean {
  if (!sessionStorage.getItem(TAB_TENANT_KEY)) return false
  const before = apiBaseUrl()
  sessionStorage.removeItem(TAB_TENANT_KEY)
  return apiBaseUrl() !== before
}

/** The backend the hub talks to: the chosen tenant's, else the default one. */
export function apiBaseUrl(): string {
  const code = selectedTenantCode()
  return tenantApis.find((api) => api.code === code)?.url ?? import.meta.env.VITE_API_BASE_URL
}

/** The tenant of the backend the hub talks to, when it is one of `tenantApis`. */
export function currentTenantCode(): number | null {
  return tenantApis.find((api) => api.url === apiBaseUrl())?.code ?? null
}

/**
 * The tenant an AdminHub "login as customer" was issued on (`?tenant=`), when the hub is
 * on another one: the handed-over token is only valid at that tenant's backend.
 */
export function handedOverTenant(): number | null {
  const code = Number(new URLSearchParams(window.location.search).get(TENANT_PARAM))
  if (!tenantApis.some((api) => api.code === code)) return null
  return code === currentTenantCode() ? null : code
}

/**
 * Another tab switched the tenant: follow it. The tenant and the session are kept per
 * browser, so a tab staying on the old backend would send it the new backend's token.
 * A tab keeping its own tenant (an AdminHub session) stays where it is.
 */
export function followTenantSwitches() {
  if (!canSwitchTenant) return
  window.addEventListener('storage', (event) => {
    if (event.key === TENANT_KEY && !sessionStorage.getItem(TAB_TENANT_KEY)) {
      window.location.reload()
    }
  })
}

let tenantsLoading: Promise<Record<number, ITenant>> | null = null

/**
 * What each tenant's backend says about itself, by tenant code — asked once per page
 * load, a tenant whose backend does not answer is left out. Asked without `httpClient`,
 * which talks to the current backend only and sends it the session's token.
 */
export function loadTenants(): Promise<Record<number, ITenant>> {
  tenantsLoading ??= Promise.allSettled(
    tenantApis.map((api) => axios.get<ITenant>(`${api.url}/options/tenant`, { timeout: 10_000 })),
  ).then((results) =>
    Object.fromEntries(
      results.flatMap((result, index) =>
        result.status === 'fulfilled' ? [[tenantApis[index].code, result.value.data]] : [],
      ),
    ),
  )
  return tenantsLoading
}
